/**
 * `<McpIntegration>` — Settings panel for per-device MCP tokens. Renders in
 * BOTH the desktop build AND the web build (Phase D+, 2026-05-19). The mint/
 * list/revoke endpoints live in the cloud backend, so token management itself
 * is universal; only the *storage* + *one-click Claude Desktop config*
 * affordances are desktop-exclusive.
 *
 * U4 (2026-07-04): split into `mcp/` — this file is the public wrapper (Clerk
 * guard + card chrome); the machinery lives in:
 *   - mcp/McpTokenPanel.tsx   — mint flow, Claude-config step, Advanced details
 *   - mcp/McpDevicesTable.tsx — active-devices list + revoke
 *   - mcp/desktopMcp.ts       — keychain / config-writer IPC helpers
 *
 * Desktop UX (zero-setup):
 *   1. Mint → cloud returns plaintext → IPC writes it to Windows Credential
 *      Manager → SPA never re-renders the plaintext.
 *   2. "Configure Claude Desktop" → IPC merges Aletheia's entry into
 *      `%APPDATA%\Claude\claude_desktop_config.json`, preserving other servers.
 *   3. Restart Claude Desktop → done.
 *
 * Web UX (manual paste):
 *   1. Mint → cloud returns plaintext → we render it ONCE with a copy button
 *      and a "this is your only chance to copy it" warning.
 *   2. User pastes into `%APPDATA%\Claude\claude_desktop_config.json` (or
 *      their MCP client of choice) — we show the exact snippet with the
 *      token already substituted in.
 *
 * Both builds share the active-devices table so a user can revoke an old
 * laptop's token from any signed-in browser.
 */

import { useCallback, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { SettingsCard } from './SettingsCard';
import { useConfirmModal } from '@/components';
import { isDesktopApp } from '@/desktop/isDesktopApp';
import { McpTokenPanel } from './mcp/McpTokenPanel';
import { McpDevicesTable } from './mcp/McpDevicesTable';

// Same guard as TopBar.tsx: when VITE_CLERK_PUBLISHABLE_KEY is unset, AppRoot
// deliberately renders WITHOUT a ClerkProvider (auth-disabled local dev /
// unconfigured deploy) and Clerk hooks THROW. This panel's unconditional
// useUser() call crashed the whole Settings surface in that mode — a latent
// bug exposed by U4's per-panel gating (previously the settings-fetch failure
// blanked the tab before this panel ever mounted).
const CLERK_ENABLED = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

export function McpIntegration(): JSX.Element {
  if (!CLERK_ENABLED) {
    return (
      <SettingsCard
        id="mcp"
        title="MCP Integration (Connect Claude Desktop)"
        desc="MCP tokens are workspace-keyed to the signed-in user. Auth is disabled in this environment (VITE_CLERK_PUBLISHABLE_KEY unset), so token management is unavailable here."
      >
        <></>
      </SettingsCard>
    );
  }
  return <McpIntegrationInner />;
}

function McpIntegrationInner(): JSX.Element {
  const desktop = isDesktopApp();
  // v0.1.14 hardening Tier 2: token storage is workspace-keyed so one OS user
  // can have multiple Aletheia workspaces side-by-side. The Clerk user id is
  // our workspace identifier in the current single-org-per-user model; switch
  // to the active org id if/when Clerk multi-org is adopted.
  const { user } = useUser();
  const workspaceId = user?.id ?? '';
  // facelift-v3 guide Phase 6: branded confirm replaces window.confirm.
  const { modal: confirmModal, confirm } = useConfirmModal();

  // A successful mint bumps this so the devices table refetches its list.
  const [devicesRefresh, setDevicesRefresh] = useState(0);
  const onMinted = useCallback(() => setDevicesRefresh((n) => n + 1), []);

  return (
    <>
      {confirmModal}
      <SettingsCard
        id="mcp-integration"
        title="MCP Integration (Connect Claude Desktop)"
        desc={
          desktop
            ? 'Expose Aletheia tools to Claude Desktop and other MCP clients. Zero setup — mint a token and click one button.'
            : 'Mint per-device tokens so Claude Desktop (or other MCP clients) can call Aletheia tools. Install the desktop app for one-click setup, or paste the snippet below into your client config.'
        }
        fullWidth
      >
        <McpTokenPanel workspaceId={workspaceId} onMinted={onMinted}>
          <McpDevicesTable refreshSignal={devicesRefresh} confirm={confirm} />
        </McpTokenPanel>
      </SettingsCard>
    </>
  );
}
